import { useState, useEffect, useCallback } from 'react';
import { getPayments, createPayment, getBookings, adminDeletePayment } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PaymentFormDialog } from '../components/payments/PaymentFormDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, CreditCard, Search, DollarSign, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Payments() {
  const { isAdmin } = useAuth();
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    booking_id: '',
    amount: '',
    payment_method: 'cash',
    coupon_code: '',
    notes: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [paymentsRes, bookingsRes] = await Promise.all([
        getPayments(),
        getBookings()
      ]);
      setPayments(paymentsRes.data);
      setBookings(bookingsRes.data.filter(b => b.status !== 'cancelled'));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        coupon_code: formData.coupon_code || undefined,
      };
      const res = await createPayment(payload);
      const p = res.data;
      const msg = p.discount > 0
        ? `Payment recorded: ${formatCurrency(p.original_amount)} - ${formatCurrency(p.discount)} discount = ${formatCurrency(p.amount)}`
        : `Payment recorded: ${formatCurrency(p.amount)}`;
      toast.success(msg);
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const resetForm = () => {
    setFormData({
      booking_id: '',
      amount: '',
      payment_method: 'cash',
      coupon_code: '',
      notes: ''
    });
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(`Permanently delete this payment of ${formatCurrency(payment.amount)}? This cannot be undone.`)) return;
    try {
      await adminDeletePayment(payment.id);
      toast.success('Payment deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const filteredPayments = payments.filter(payment => {
    const guestName = payment.guest?.name?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return guestName.includes(query);
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate total revenue
  const totalRevenue = payments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Payments</h1>
          <p className="text-stone-500 mt-1">Track and manage payment records</p>
        </div>
        <Button 
          onClick={openAddModal}
          className="bg-sage-700 hover:bg-sage-800 text-white rounded-sm"
          data-testid="add-payment-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Record Payment
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-md">
              <DollarSign className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900 font-['Manrope']">{formatCurrency(totalRevenue)}</p>
              <p className="text-sm text-stone-500">Total Revenue</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-md">
              <CreditCard className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900 font-['Manrope']">{payments.length}</p>
              <p className="text-sm text-stone-500">Total Transactions</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sage-50 rounded-md">
              <DollarSign className="w-5 h-5 text-sage-700" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900 font-['Manrope']">
                {formatCurrency(payments.length > 0 ? totalRevenue / payments.length : 0)}
              </p>
              <p className="text-sm text-stone-500">Avg. Transaction</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search by guest name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-stone-300"
            data-testid="search-payments-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">No payments found</p>
            <Button 
              onClick={openAddModal}
              variant="outline"
              className="mt-4"
            >
              Record your first payment
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50 hover:bg-stone-50">
                <TableHead className="font-semibold text-stone-700">Date</TableHead>
                <TableHead className="font-semibold text-stone-700">Guest</TableHead>
                <TableHead className="font-semibold text-stone-700">Booking Period</TableHead>
                <TableHead className="font-semibold text-stone-700">Method</TableHead>
                <TableHead className="font-semibold text-stone-700">Amount</TableHead>
                <TableHead className="font-semibold text-stone-700">Status</TableHead>
                {isAdmin && <TableHead className="font-semibold text-stone-700 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment, index) => (
                <TableRow 
                  key={payment.id}
                  className="table-row-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`payment-row-${payment.id}`}
                >
                  <TableCell className="text-stone-600">{formatDate(payment.created_at)}</TableCell>
                  <TableCell className="font-medium text-stone-900">{payment.guest?.name || '-'}</TableCell>
                  <TableCell className="text-stone-600">
                    {payment.booking ? `${payment.booking.check_in_date} to ${payment.booking.check_out_date}` : '-'}
                  </TableCell>
                  <TableCell className="capitalize text-stone-600">{payment.payment_method.replace('_', ' ')}</TableCell>
                  <TableCell className="font-medium text-stone-900">
                    {formatCurrency(payment.amount)}
                    {payment.discount > 0 && (
                      <span className="block text-xs text-emerald-600">-{formatCurrency(payment.discount)} ({payment.coupon_code})</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`status-badge status-${payment.status}`}>
                      {payment.status}
                    </span>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDeletePayment(payment)}
                        className="h-8 px-2 text-stone-500 hover:text-red-700 hover:bg-red-50"
                        title="Permanently delete (admin only)"
                        data-testid={`delete-payment-${payment.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <PaymentFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        bookings={bookings}
      />
    </div>
  );
}
